/**
 * What it is like to BE each animal.
 *
 * The premise of the game is Kate's: "you try to survive and have fun being different
 * animals." Until now the animals differed only in speed, strength and jump numbers —
 * four of the thirteen were mechanically identical. Playing a bunny felt exactly like
 * playing a bear, slower.
 *
 * This gives each animal a role in the world rather than a stat line: what hunts it,
 * what it hunts, what it can eat, and what it can do that nothing else can. The point is
 * that changing animal should change what you *do*, not how fast you do it.
 */
import type { SpeciesId } from './species'

/** Where an animal sits in the food chain. Drives who chases whom, including the player. */
export type Role =
  | 'apex'      // nothing hunts it
  | 'predator'  // hunts, and is hunted by apex
  | 'prey'      // hunted by everything that hunts
  | 'critter'   // small prey, but hard to catch
  | 'neutral'   // not part of the chain

export interface Identity {
  role: Role
  /** Species this one will hunt when it is AI, and can kill quickly when you play it. */
  hunts: SpeciesId[]
  /** Species that will hunt this one. When you play it, these come for you. */
  huntedBy: SpeciesId[]
  /** What restores its hunger. Meat-eaters cannot graze; grazers cannot eat meat. */
  diet: 'meat' | 'plants' | 'both'
  /** One line telling the player what they now are. Shown on morph. */
  feel: string
  /** How much noise it makes — loud animals are noticed from further away. */
  noise: number
  /** How alert it is: how far it can notice something itself. */
  senses: number
}

export const IDENTITY: Record<SpeciesId, Identity> = {
  human: {
    role: 'neutral', hunts: [], huntedBy: ['bear', 'wolf'],
    diet: 'both', noise: 1, senses: 1,
    feel: 'Weak but clever. Buy a morph tool and become something better.',
  },

  bear: {
    // Kate: strongest thing on land. So nothing should frighten it.
    role: 'apex', hunts: ['deer', 'bunny', 'wolf', 'human', 'cat', 'dog'],
    huntedBy: [], diet: 'both', noise: 2.2, senses: 1.1,
    feel: 'Nothing hunts a bear. Slow and loud, but everything runs from you.',
  },

  wolf: {
    role: 'predator', hunts: ['deer', 'bunny', 'human', 'cat'],
    huntedBy: ['bear'], diet: 'meat', noise: 0.8, senses: 1.9,
    feel: 'Crouch to smell where prey went. You are built to chase things down.',
  },

  deer: {
    // Kate never gave the deer a power. Its role IS the answer: the hunted one.
    role: 'prey', hunts: [], huntedBy: ['bear', 'wolf', 'cat', 'human'],
    diet: 'plants', noise: 0.7, senses: 2.2,
    feel: 'Everything wants to eat you. You hear them coming, and you are fast.',
  },

  bunny: {
    role: 'critter', hunts: [], huntedBy: ['bear', 'wolf', 'cat', 'dog', 'human'],
    diet: 'plants', noise: 0.25, senses: 1.6,
    feel: 'You cannot fight anything. Hop, hide, and dig where nothing can follow.',
  },

  cat: {
    role: 'predator', hunts: ['bunny', 'bird'],
    huntedBy: ['bear', 'wolf'], diet: 'meat', noise: 0.3, senses: 1.7,
    feel: 'Quiet and quick. Climb trees, and you are the only thing a bird fears.',
  },

  dog: {
    role: 'predator', hunts: ['bunny', 'deer'],
    huntedBy: ['bear'], diet: 'meat', noise: 1.4, senses: 1.5,
    feel: 'Loud and loyal. Team up with a human and hunt together.',
  },

  bird: {
    role: 'critter', hunts: [], huntedBy: ['cat'],
    diet: 'plants', noise: 0.4, senses: 2.6,
    feel: 'Get above the trees and see everything. On the ground you are helpless.',
  },

  chameleon: {
    role: 'critter', hunts: [], huntedBy: ['bird', 'cat', 'wolf'],
    diet: 'plants', noise: 0.1, senses: 1.2,
    feel: 'Too slow to run. Hold completely still and nothing can see you.',
  },

  camel: {
    role: 'neutral', hunts: [], huntedBy: ['wolf'],
    diet: 'plants', noise: 1.1, senses: 1.3,
    feel: 'Built for the desert. You can go a long time without drinking.',
  },

  dolphin: {
    role: 'predator', hunts: ['fish'], huntedBy: [],
    diet: 'meat', noise: 0.9, senses: 2.0,
    feel: 'Fast in the water and afraid of nothing in it.',
  },
  whale: {
    role: 'apex', hunts: ['fish'], huntedBy: [],
    diet: 'meat', noise: 1.8, senses: 1.4,
    feel: 'The biggest thing in the ocean. Nothing troubles you.',
  },
  fish: {
    role: 'prey', hunts: [], huntedBy: ['dolphin', 'whale'],
    diet: 'plants', noise: 0.2, senses: 1.3,
    feel: 'Small and quick. Stay in the shoal and out of open water.',
  },
}

/** Would `hunter` chase `quarry`? */
export function hunts(hunter: SpeciesId, quarry: SpeciesId): boolean {
  return IDENTITY[hunter].hunts.includes(quarry)
}

/** Can this animal eat that kind of food? A deer cannot eat meat. */
export function canEat(species: SpeciesId, food: 'meat' | 'plants'): boolean {
  const diet = IDENTITY[species].diet
  return diet === 'both' || diet === food
}

/**
 * How far away this animal notices things, and how far away it is noticed. A bear is
 * loud and obvious; a chameleon is neither.
 */
export function noticeRange(species: SpeciesId, base: number): number {
  return base * IDENTITY[species].senses
}

export function noticedRange(species: SpeciesId, base: number): number {
  return base * IDENTITY[species].noise
}
